import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../api/client";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Camera, Trash, ArrowsHorizontal } from "@phosphor-icons/react";
import { BRAND } from "../Brand";

export default function BaselinesTab({ projectId, canEdit }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [compare, setCompare] = useState(null); // {baseline, rows}

  const load = useCallback(async () => {
    if (!projectId) { setRows([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/plan/baselines", { params: { project_id: projectId } });
      setRows(data.baselines || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const { data } = await api.post("/plan/baselines", { project_id: projectId, name: name.trim() || undefined });
      setName("");
      load();
      toast.success(`Baseline "${data.name}" saved (${data.task_count} tasks)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete baseline "${b.name}"?`)) return;
    try {
      await api.delete(`/plan/baselines/${b.id}`);
      if (compare?.baseline?.id === b.id) setCompare(null);
      load();
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const showCompare = async (b) => {
    try {
      const { data } = await api.get(`/plan/baselines/${b.id}/compare`);
      setCompare(data);
    } catch { toast.error("Compare failed"); }
  };

  const varianceColor = (n) => {
    if (n == null) return "text-neutral-400";
    if (n === 0) return "text-neutral-500";
    if (n > 0) return "text-red-600";
    return "text-emerald-600";
  };

  return (
    <div className="flex-1 overflow-auto p-6" data-testid="baselines-tab">
      <div className="max-w-5xl">
        {canEdit ? (
          <div className="bg-white border border-neutral-200 rounded-xl p-4 mb-5 flex items-end gap-3">
            <div className="flex-1">
              <label className="text-[10px] uppercase tracking-widest font-semibold text-neutral-600">New baseline name (optional)</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 mt-1" placeholder="e.g. Kickoff plan" data-testid="baseline-name-input" />
            </div>
            <Button className="text-white h-9" style={{ backgroundColor: BRAND.colors.blue }} onClick={save} data-testid="baseline-save-btn">
              <Camera size={13} weight="bold" className="mr-1.5" /> Snapshot current plan
            </Button>
          </div>
        ) : null}

        <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr className="text-[10px] uppercase tracking-widest text-neutral-500">
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Saved</th>
                <th className="text-right px-3 py-2 w-24">Tasks</th>
                <th className="w-56 text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                <tr><td colSpan={4} className="text-center py-8 text-neutral-400 text-sm">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={4} className="text-center py-8 text-neutral-400 text-sm">No baselines yet.</td></tr>
              ) : rows.map((b) => (
                <tr key={b.id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`baseline-row-${b.id}`}>
                  <td className="px-3 py-2 font-semibold text-neutral-800">{b.name}</td>
                  <td className="px-3 py-2 text-neutral-500 text-xs">
                    {b.created_by} · {new Date(b.created_at).toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-neutral-600">{b.task_count}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button variant="outline" size="sm" onClick={() => showCompare(b)} data-testid={`baseline-compare-${b.id}`} className="h-8">
                        <ArrowsHorizontal size={13} weight="bold" className="mr-1.5" /> Compare
                      </Button>
                      {canEdit ? (
                        <button onClick={() => remove(b)} className="h-8 w-8 flex items-center justify-center text-red-500 hover:text-red-700 rounded" data-testid={`baseline-delete-${b.id}`}>
                          <Trash size={13} weight="bold" />
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {compare ? (
          <div className="mt-6 bg-white border border-neutral-200 rounded-xl overflow-hidden" data-testid="baseline-compare-panel">
            <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 bg-neutral-50">
              <div>
                <div className="text-sm font-black" style={{ fontFamily: "Chivo, sans-serif" }}>
                  Baseline vs current — {compare.baseline.name}
                </div>
                <div className="text-[11px] text-neutral-500 font-mono">
                  Saved {new Date(compare.baseline.created_at).toLocaleString()}
                </div>
              </div>
              <button onClick={() => setCompare(null)} className="text-xs text-neutral-500 hover:text-neutral-900">Close</button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white border-b border-neutral-200">
                  <tr className="text-[10px] uppercase tracking-widest text-neutral-500">
                    <th className="text-left px-3 py-2">Task</th>
                    <th className="text-left px-3 py-2">Baseline start</th>
                    <th className="text-left px-3 py-2">Current start</th>
                    <th className="text-right px-3 py-2">Δ Start</th>
                    <th className="text-left px-3 py-2">Baseline finish</th>
                    <th className="text-left px-3 py-2">Current finish</th>
                    <th className="text-right px-3 py-2">Δ Finish</th>
                  </tr>
                </thead>
                <tbody>
                  {compare.rows.map((r) => (
                    <tr key={r.id} className="border-b border-neutral-100">
                      <td className="px-3 py-2 text-neutral-800">{r.name}{r.removed ? <span className="ml-2 text-[10px] text-red-600 font-bold">DELETED</span> : null}</td>
                      <td className="px-3 py-2 text-neutral-600 font-mono">{r.baseline_start || "—"}</td>
                      <td className="px-3 py-2 text-neutral-600 font-mono">{r.current_start || "—"}</td>
                      <td className={`px-3 py-2 text-right font-mono ${varianceColor(r.start_variance_days)}`}>
                        {r.start_variance_days == null ? "—" : `${r.start_variance_days > 0 ? "+" : ""}${r.start_variance_days}d`}
                      </td>
                      <td className="px-3 py-2 text-neutral-600 font-mono">{r.baseline_finish || "—"}</td>
                      <td className="px-3 py-2 text-neutral-600 font-mono">{r.current_finish || "—"}</td>
                      <td className={`px-3 py-2 text-right font-mono ${varianceColor(r.finish_variance_days)}`}>
                        {r.finish_variance_days == null ? "—" : `${r.finish_variance_days > 0 ? "+" : ""}${r.finish_variance_days}d`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
